<script setup>
import { defineProps, defineEmits, watch, toRef, ref } from 'vue';
import { useToast } from 'vue-toastification';
import { API_ENDPOINTS } from '@/apiConfig';
import { useLondonUnixTime } from '@/composables/useLondonTime';
import axiosClient from '@/axiosClient';
import errorCustomIcon from '../assets/error-cross.jpg';
import { useFetchKioskNotLogout } from '@/composables/api/kiosk/useFetchKioskNotLogout';
import { useFetchKioskRoster } from '@/composables/api/kiosk/useFetchKioskRoster';
import { useEmployees } from '@/composables/api/useEmployees';
import createCustomToastContent from '@/composables/createCustomToastContent';

const props = defineProps({
  cardNumber: {
    type: String,
    default: ''
  },
  manualCardNumber: {
    type: String,
    default: ''
  },
  shiftName: {
    type: String,
    default: 'SHIFT'
  }
});

const emit = defineEmits(['processComplete', 'error', 'clearCard']);
const { getLondonUnixTime } = useLondonUnixTime();
const employeeNotFoundIcon = errorCustomIcon;
const toast = useToast();
const localShiftName = toRef(props, 'shiftName');
const cooldownCards = ref(new Set());
const activeToasts = ref({});

const { data: notLogoutData, error: notLogoutError, fetchKioskNotLogout } = useFetchKioskNotLogout();
const { data: rosterData, error: rosterError, fetchKioskRoster } = useFetchKioskRoster();
const { employeesList } = useEmployees();

// Updated Toast function
function showToast(cardNumber, content, type = 'info', timeout = 8000, position = "bottom-right", icon = false) {
  if (activeToasts.value[cardNumber]) {
    toast.dismiss(activeToasts.value[cardNumber]);
  }
  const toastOptions = {
    timeout,
    icon,
    draggable: false,
    position,
    onClose: () => {
      delete activeToasts.value[cardNumber];
    }
  };
  activeToasts.value[cardNumber] = toast[type](content, toastOptions);
}

// Check if employee exists
const checkEmployeeExists = (cardNumber) => {
  const employee = employeesList.value.find(employee => employee.employee_badge_id === cardNumber);
  return employee || null;
};

// Process the card
async function processCard(cardNumber) {
  if (!/^\d{10}$/.test(cardNumber)) {
    const customContent = createCustomToastContent('', '', "Not supported card!", "search-response-toast");
    showToast(cardNumber, customContent, 'info', 6000, "bottom-center", true);
    emit('error', 'Invalid card number');
    return;
  }

  if (cooldownCards.value.has(cardNumber)) {
    const customContent = createCustomToastContent('', '', "Double tap not allowed, please wait 10 seconds.", "search-response-toast");
    showToast(cardNumber, customContent, 'info', 6000, "bottom-center", true);
    emit('clearCard', 0);
    return;
  }

  cooldownCards.value.add(cardNumber);
  setTimeout(() => {
    cooldownCards.value.delete(cardNumber);
  }, 10000);

  const currentShiftName = localShiftName.value;
  const employee = checkEmployeeExists(cardNumber);

  if (!employee) {
    const customContent = createCustomToastContent(`Card# ${cardNumber}`, employeeNotFoundIcon, "No Employee Found!");
    showToast(cardNumber, customContent, 'error');
    emit('error', 'Employee not found');
    return;
  }

  const customContent = createCustomToastContent(employee.employee_name, employee.employee_photo_url, "Processing...");
  showToast(cardNumber, customContent, 'info', 10000);

  try {
    const updatedTime = getLondonUnixTime();
    
    await fetchKioskNotLogout(employee.employee_id, currentShiftName);
    
    if (notLogoutError.value) {
      throw new Error(notLogoutError.value);
    }

    if (notLogoutData.value) {
      await handleClockOut(notLogoutData.value, updatedTime, employee);
      return;
    }

    await fetchKioskRoster(employee.employee_id, currentShiftName);
    
    if (rosterError.value) {
      throw new Error(rosterError.value);
    }

    if (rosterData.value) {
      await handleClockIn(rosterData.value, updatedTime, employee);
    } else {
      handleNoShiftFound(cardNumber, employee);
    }
  } catch (error) {
    handleNetworkOrServerError(error, employee, cardNumber);
    emit('error', 'Network error during processing');
  }
}

async function handleClockOut(notLogoutUser, updatedTime, employee) {
  const postData = {
    attendance_linked_id: notLogoutUser.attendance_id,
    employee_badge_id: employee.employee_badge_id,
    employee_id: notLogoutUser.employee_id,
    employee_company_id: notLogoutUser.employee_company_id,
    roster_id: notLogoutUser.roster_id,
    attendance_timestamp: updatedTime,
    roster_end: notLogoutUser.roster_end,
    shift_type: notLogoutUser.shift_type
  };
  try {
    const response = await axiosClient.post(API_ENDPOINTS.clockOut, postData);
    handleClockOutResponse(response, employee);
    emit('processComplete', employee.employee_badge_id);
  } catch (error) {
    handleNetworkOrServerError(error, employee, employee.employee_badge_id);
  }
}

async function handleClockIn(rosterData, updatedTime, employee) {
  const postData = {
    employee_badge_id: employee.employee_badge_id,
    employee_id: rosterData.employee_id,
    employee_company_id: rosterData.employee_company_id,
    roster_id: rosterData.roster_id,
    attendance_timestamp: updatedTime,
    roster_start: rosterData.roster_start,
    shift_type: rosterData.shift_type
  };
  try {
    const response = await axiosClient.post(API_ENDPOINTS.clockIn, postData);
    handleClockInResponse(response, employee);
    emit('processComplete', employee.employee_badge_id);
  } catch (error) {
    handleNetworkOrServerError(error, employee, employee.employee_badge_id);
  }
}

function handleClockOutResponse(response, employee) {
  const message = `${response.data.message} ${response.data.attendance_status}` || "No status provided by server";
  const customContent = createCustomToastContent(employee.employee_name, employee.employee_photo_url, message);
  showToast(employee.employee_badge_id, customContent, response.status === 200 ? 'success' : 'warning');
}

function handleClockInResponse(response, employee) {
  const message = `${response.data.message} ${response.data.attendance_status}` || "No status provided by server";
  const customContent = createCustomToastContent(employee.employee_name, employee.employee_photo_url, message);
  showToast(employee.employee_badge_id, customContent, response.status === 200 ? 'success' : (response.status === 201 ? 'warning' : 'error'));
}

function handleNetworkOrServerError(error, employee) {
  let message = '';
  if (error.response) {
    console.error(`Server error (${error.response.status}):`, error.response.data);
    message = error.response.data.message || `Server responded with error code ${error.response.status}`;
  } else if (error.request) {
    console.error("No response from server", error.request);
    message = "No response from server";
  } else {
    console.error("Error in sending request:", error.message);
    message = "Error in sending request";
  }
  const customContent = createCustomToastContent(employee ? employee.employee_name : "Error", null, message);
  showToast(employee.employee_badge_id, customContent, 'error');
}

function handleNoShiftFound(cardNumber, employee) {
  const customContent = createCustomToastContent(employee.employee_name, employeeNotFoundIcon, "No shift found.");
  showToast(cardNumber, customContent, 'error');
}

// Watch for changes in both cardNumber and manualCardNumber props
watch(() => props.cardNumber, (newCardNumber) => {
  if (newCardNumber) {
    processCard(newCardNumber);
  }
});

watch(() => props.manualCardNumber, (newManualCardNumber) => {
  if (newManualCardNumber) {
    processCard(newManualCardNumber);
  }
});
</script>

<style>
.Vue-Toastification__icon{
  margin-left: 26px !important; 
  width: 26px !important;
}

/* Kiosk Toast */
.employee-response-toast{
  min-height: 70px;
}

/* Global Css for Toast */
.Vue-Toastification__toast{
  padding: 0 !important;
  width: 450px;
}
.Vue-Toastification__close-button{
  padding-right: 20px !important;
}

/* Employee Response Toast */
.employee-response-toast .toast-photo{
  float: left;
  width: 20%;
  height: auto;
}
.employee-response-toast .toast-photo img{
  width: 80px;
  height: auto;  
}
.employee-response-toast .message-box{
  float: right;
  width: 78%;
  line-height: 1;
}
.employee-response-toast .toast-header{
  text-transform: uppercase;
  display: block;
  font-size: 20px;
  margin-bottom: 6px;
  margin-top: 11px;
}
.employee-response-toast .toast-message{
  font-size: 24px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 0;
  margin: 0;
}

/* Other API Response Toast */
.other-response-toast .toast-photo{
  float: left;
  width: 20%;
}
.other-response-toast .toast-photo img{
  width: 80px;
  height: auto;  
}
.other-response-toast .message-box{
  float: right;
  width: 78%;
}
.other-response-toast .toast-header{
  text-transform: uppercase;
  display: block;
  font-size: 20px;
  margin-bottom: 2px;
  margin-top: 12px;
}
.other-response-toast .toast-message{
  font-size: 26px;
  text-transform: uppercase;
}

/* Other API Response Toast */
.search-response-toast .toast-photo{
  float: left;
  width: 20%;
  display: none
}
.search-response-toast .toast-photo img{
  width: 80px;
  height: auto;  
}
.search-response-toast .message-box{
  float: right;
  width: 100%;
}
.search-response-toast .toast-header{
  text-transform: uppercase;
  display: block;
  font-size: 20px;
  margin-bottom: 2px;
  margin-top: 12px;
}
.search-response-toast .toast-message{
  font-size: 22px;
  text-transform: uppercase;
}

/* Other API Response Toast */
.error-response-toast .toast-photo{
  float: left;
  width: 20%;
}
.error-response-toast .toast-photo img{
  width: 80px;
  height: auto;  
}
.error-response-toast .message-box{
  float: right;
  width: 78%;
}
.error-response-toast .toast-header{
  text-transform: uppercase;
  display: block;
  font-size: 20px;
  margin-bottom: 2px;
  margin-top: 12px;
}
.error-response-toast .toast-message{
  font-size: 24px;
  text-transform: uppercase;
}
</style>